import { useEffect, useRef, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import type { RelatieGegevens, RelatieWijzigVelden } from '../../../gedeeld/types'
import { c, Fout } from '../../lib/api'
import { EXACT_URL } from './RelatieFormulier'

/* ===========================================================================
 *  Een bestaande relatie wijzigen, op de kaart van een voorstel
 *
 *  Wat Cowork voorstelt staat ingevuld; wat er nu in Exact staat staat eronder
 *  ("was: ..."), bij elk veld dat anders is. Rechtsboven: wat er verandert,
 *  zoals de server het uitrekent met dezelfde code als bij Uitvoeren.
 *
 *  Welke relatie en welke bv kun je hier NIET veranderen: dat staat vast in
 *  het voorstel, en de server neemt het daaruit -- een kaart die je naar een
 *  andere relatie kunt omleiden, is een kaart die de verkeerde wijzigt.
 * =========================================================================== */

/*
 * Elk veld dat de AI kan invullen (voorstel_relatie_wijzigen in gereedschap.ts
 * van de functie cowork) staat hier open -- ook soort, BIC en "op naam van".
 * Stonden die er niet, dan ging een misgelezen BIC ongezien mee naar Exact.
 */
const VELDEN: { k: keyof RelatieGegevens; label: string; mono?: boolean; breed?: boolean }[] = [
  { k: 'naam', label: 'Naam', breed: true },
  { k: 'soort', label: 'Soort' },
  { k: 'land', label: 'Land', mono: true },
  { k: 'adres', label: 'Straat en huisnummer', breed: true },
  { k: 'postcode', label: 'Postcode', mono: true },
  { k: 'plaats', label: 'Plaats' },
  { k: 'email', label: 'E-mail' },
  { k: 'telefoon', label: 'Telefoon' },
  { k: 'btw', label: 'Btw-nummer', mono: true },
  { k: 'kvk', label: 'KvK-nummer', mono: true },
  { k: 'iban', label: 'IBAN', mono: true, breed: true },
  { k: 'bic', label: 'BIC', mono: true },
  { k: 'tenaamstelling', label: 'Op naam van' },
  { k: 'website', label: 'Website' },
  { k: 'notitie', label: 'Notitie in Exact', breed: true },
]

const SOORTEN: { key: RelatieGegevens['soort']; label: string }[] = [
  { key: 'leverancier', label: 'Leverancier' },
  { key: 'klant', label: 'Klant' },
  { key: 'beide', label: 'Beide' },
]

export default function RelatieWijzigFormulier({ v, onV }: { v: RelatieWijzigVelden; onV: (v: RelatieWijzigVelden) => void }) {
  const [rekent, setRekent] = useState(false)
  const [fouten, setFouten] = useState<Record<string, string>>({})
  /* Wat de server zei als hij het niet per veld kon zeggen. */
  const [algemeen, setAlgemeen] = useState<string | null>(null)
  const r = v.relatie
  const was = v.huidig.relatie
  const zet = (k: keyof RelatieGegevens, w: string) => onV({ ...v, relatie: { ...r, [k]: w } })

  /* De kaart zoals hij NU is. Het antwoord van het nakijken komt een tel
     later binnen; wie intussen "ook in andere bv's" of de IBAN-bevestiging
     aanvinkte (die tellen niet mee in de sleutel hieronder), mag dat niet
     kwijtraken doordat het antwoord over een oude kaart heen wordt gezet. */
  const nu = useRef(v)
  nu.current = v

  /* Na een aanpassing: opnieuw laten uitrekenen wat er verandert. */
  const sleutel = JSON.stringify([r, v.actief, v.geblokkeerd])
  const [eerste] = useState(sleutel)
  useEffect(() => {
    if (sleutel === eerste) return
    let weg = false
    const t = setTimeout(() => {
      setRekent(true)
      c('relatie-wijzigen-nakijken', { division: v.division, id: v.id, modified: v.modified, relatie: r, actief: v.actief, geblokkeerd: v.geblokkeerd })
        .then((n) => {
          if (weg) return
          setFouten({})
          setAlgemeen(null)
          onV({ ...nu.current, wat: n.wat, waarschuwingen: [...n.waarschuwingen, ...(n.verouderd ? ['Iemand heeft hem intussen in Exact gewijzigd: wijs dit af en vraag Cowork opnieuw.'] : [])], nieuweIban: n.nieuweIban })
        }, (e) => {
          if (weg) return
          /* Fouten per veld (een IBAN dat niet klopt) komen bij dat veld;
             de rest -- geen recht, Exact onbereikbaar -- erboven. Eerder
             verdween een fout hier zonder spoor. */
          const per = e instanceof Fout ? e.fouten ?? {} : {}
          setFouten(per)
          setAlgemeen(Object.keys(per).length ? null : e instanceof Error ? e.message : String(e))
        })
        .finally(() => { if (!weg) setRekent(false) })
    }, 700)
    return () => { weg = true; clearTimeout(t) }
  }, [sleutel]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Een fout bij een veld dat hier niet staat, mag niet onzichtbaar blijven. */
  const losseFouten = Object.entries(fouten).filter(([k]) => !VELDEN.some((x) => x.k === k))

  return (
    <div className="formulier compact relatie-kaart">
      <p className="breed stil">
        <a href={`${EXACT_URL}/relatie/${v.division}/${encodeURIComponent(v.code)}`} target="_blank" rel="noreferrer">{was.naam}</a>
        {' '}· {v.bvNaam} · code <span className="mono">{v.code}</span>
      </p>

      <div className="veld breed">
        <span className="veld-label">Status</span>
        <div className="wijzig-status">
          <div className="segment" role="radiogroup" aria-label="Actief of inactief">
            <button type="button" role="radio" aria-checked={v.actief && !v.verwijderen} className={`segment-knop ${v.actief && !v.verwijderen ? 'aan' : ''}`} onClick={() => onV({ ...v, actief: true, verwijderen: false })}>Actief</button>
            <button type="button" role="radio" aria-checked={!v.actief && !v.verwijderen} className={`segment-knop ${!v.actief && !v.verwijderen ? 'aan' : ''}`} onClick={() => onV({ ...v, actief: false, verwijderen: false })}>Inactief</button>
            <button type="button" role="radio" aria-checked={v.verwijderen} className={`segment-knop t-danger ${v.verwijderen ? 'aan' : ''}`} onClick={() => onV({ ...v, verwijderen: true })}>Verwijderen</button>
          </div>
          <label className="vinkje"><input type="checkbox" checked={v.geblokkeerd} onChange={(e) => onV({ ...v, geblokkeerd: e.target.checked })} /> Geblokkeerd</label>
        </div>
        {v.actief !== v.huidig.actief && !v.verwijderen && <span className="wijzig-was">was: {v.huidig.actief ? 'actief' : 'inactief'}</span>}
      </div>

      {v.verwijderen && (
        <div className="relatie-waarschuwing breed">
          <p><AlertTriangle size={13} /> Hij gaat helemaal weg uit Exact{v.ook.length ? `, ook in ${v.ook.length} andere bv${v.ook.length === 1 ? '' : "'s"}` : ''}. Dat lukt alleen als er nog niets op geboekt is; anders zegt Exact nee en zet je hem op inactief.</p>
        </div>
      )}

      {!v.verwijderen && algemeen && (
        <p className="veld-fout breed" role="alert"><AlertTriangle size={13} /> {algemeen}</p>
      )}
      {!v.verwijderen && losseFouten.length > 0 && (
        <div className="relatie-waarschuwing breed" role="alert">
          {losseFouten.map(([k, f]) => <p key={k}><AlertTriangle size={13} /> {f}</p>)}
        </div>
      )}

      {!v.verwijderen && VELDEN.map(({ k, label, mono, breed }) => (
        <label key={k} className={`veld ${breed ? 'breed' : ''}`}>
          <span className="veld-label">{label}</span>
          {k === 'notitie'
            ? <textarea className="invoer" rows={2} value={r[k]} onChange={(e) => zet(k, e.target.value)} aria-invalid={!!fouten[k]} />
            : k === 'soort'
              ? (
                <select className="invoer" value={r.soort} onChange={(e) => zet('soort', e.target.value)} aria-invalid={!!fouten[k]}>
                  {SOORTEN.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              )
              : <input className={`invoer ${mono ? 'mono' : ''}`} value={String(r[k] ?? '')} onChange={(e) => zet(k, e.target.value)} aria-invalid={!!fouten[k]} />}
          {fouten[k] && <span className="veld-fout" role="alert">{fouten[k]}</span>}
          {String(r[k] ?? '').trim() !== String(was[k] ?? '').trim() && (
            <span className="wijzig-was">was: {k === 'soort' ? SOORTEN.find((s) => s.key === was.soort)?.label ?? was.soort : String(was[k] ?? '') || '(leeg)'}</span>
          )}
        </label>
      ))}

      {!v.verwijderen && <div className="veld breed">
        <span className="veld-label">Wat verandert{rekent ? ' (rekent…)' : ''}</span>
        {v.wat.length ? <ul className="wijzig-lijst">{v.wat.map((w) => <li key={w}>{w}</li>)}</ul> : <span className="stil">Nog niets.</span>}
      </div>}

      {!v.verwijderen && v.waarschuwingen.length > 0 && (
        <div className="relatie-waarschuwing breed">
          {v.waarschuwingen.map((w) => <p key={w}><AlertTriangle size={13} /> {w}</p>)}
        </div>
      )}
      {!v.verwijderen && v.nieuweIban && (
        <label className="vinkje iban-bevestig breed">
          <input type="checkbox" checked={v.ibanBevestigd} onChange={(e) => onV({ ...v, ibanBevestigd: e.target.checked })} />
          <span>Ik heb het nieuwe rekeningnummer nagebeld op een nummer dat we al hadden, niet op een nummer uit de mail of factuur.</span>
        </label>
      )}

      {v.tweelingen.length > 0 && (
        <div className="veld breed">
          <span className="veld-label">{v.verwijderen ? 'Ook verwijderen in' : "Ook in andere bv's"}</span>
          <div className="relatie-bvs">
            {v.tweelingen.map((t) => (
              <div key={t.id} className="relatie-bv">
                <label className="vinkje">
                  <input type="checkbox" checked={v.ook.includes(t.id)} onChange={(e) => onV({ ...v, ook: e.target.checked ? [...v.ook, t.id] : v.ook.filter((x) => x !== t.id) })} />
                  <span>{t.bvNaam}</span>
                </label>
                <div className="relatie-stand"><span className="stil">{t.code} {t.naam} · zelfde {t.op}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
